import {Component, OnInit,
  CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {GraphqlEdgeService} from '@app/services/dcp-graphql-edge';
import {ConfigService, LoadingPageComponent} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {AuthStorageService, Country, SiteConnector, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {DcpProductDetailsService} from '@app/services/dcp-product-details';
import {ProductDetails} from '@app/model/dcp-product-details';
import {convertUrlToCurrentHostname} from './../shared/url-utils';
import {getAuthorized} from './../shared/getAuthorized';
import {TabNavigationComponent} from '../tab-navigation/tab-navigation.component';
import {NgTemplateOutlet, AsyncPipe} from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'dcp-products-page',
  templateUrl: './products-page.component.html',
  styleUrls: ['./products-page.component.scss'],
  imports: [LoadingPageComponent, TabNavigationComponent, NgTemplateOutlet, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductsPageComponent implements OnInit {
  technicalName = '';
  materialNumber = '';
  currentUser: string;
  queryType: string;
  isAuthorized: boolean;
  user$: Observable<User>;
  loadingState = false;
  loadingScreenText = 'Please wait while the product is updated';
  currentUserScimUserId: string;
  productResult$: Observable<any>; // Should be Product, but using any for now
  productDetails$: Observable<ProductDetails>;
  countries$: Observable<Country[]>;
  countrySelected = 'US';
  productView = 'tree';

  constructor(
    private authStorageService: AuthStorageService,
    private configService: ConfigService,
    private graphqlEdgeService: GraphqlEdgeService,
    private productDetailsService: DcpProductDetailsService,
    private route: ActivatedRoute,
    private siteConnector: SiteConnector,
    private userFacade: UserAccountFacade,
  ) {}

  /**
   * Angular lifecycle hook.
   * Initializes the component, loads user data, checks authorization, and loads countries.
   */
  ngOnInit(): void {
    this.user$ = this.userFacade.get();
    this.user$.pipe(untilDestroyed(this), take(1)).subscribe((user) => {
      this.currentUserScimUserId = user.scimUserId;
    });
    this.getAuthorizedRoles();
    this.route.params.pipe(untilDestroyed(this)).subscribe((params) => {
      if (params?.hasOwnProperty('productId')) {
        this.technicalName = params['productId'];
        this.getProduct(this.technicalName);
      }
    });
    this.countries$ = this.getCountries();
  }

  /**
   * Retrieves the list of available countries from the site connector.
   * @returns Observable containing an array of Country objects.
   */
  getCountries(): Observable<Country[]> {
    return this.siteConnector.getCountries();
  }

  /**
   * Handles input events from text fields and updates the corresponding properties.
   * @param event CustomEvent containing input data.
   */
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.id === 'technicalName') {
      this.technicalName = target.value;
    }
  }

  /**
   * Handles selection of the product query type from the UI.
   * @param event CustomEvent containing selection data.
   */
  onInputProductQueryType(event: CustomEvent): void {
    this.queryType = event.detail.selectedItems?.[0].getAttribute('value');
    if (this.queryType === 'BASIC' || this.queryType === 'FULL') {
      this.getProduct(this.technicalName, this.queryType);
      this.productView = 'tree';
    } else {
      this.getProduct(this.technicalName, 'FULL');
      this.getProductDetails(this.technicalName, this.countrySelected ?? 'US');
      this.productView = 'preview';
    }
  }

  /**
   * Handles selection of the country from the UI.
   * @param event CustomEvent containing selection data.
   */
  onInputCountry(event: CustomEvent): void {
    this.countrySelected = event.detail.item.value;
  }

  /**
   * Resets the product query and clears the input fields.
   */
  onReset() {
    this.technicalName = '';
    this.productResult$ = undefined;
  }

  /**
   * Fetches product data by technical name and query type using the GraphQL edge service.
   * @param technicalName The technical name of the product.
   * @param queryType The type of product query.
   * @returns Observable containing the product data as an object.
   */
  private fetchProduct(technicalName: string, queryType: string): Observable<any> {
    return this.graphqlEdgeService.getProductByTechnicalName(technicalName, queryType).pipe(
      map((response: any) => {
        const products = response?.data?.showCosmosDbProductsV3?.products || [];
        return products.length ? products[0] : undefined;
      }),
    );
  }

  /**
   * Initiates the product query and sets the result as an Observable.
   * @param technicalName The technical name of the product.
   * @param queryType The type of product query. Defaults to 'BASIC'.
   */
  getProduct(technicalName: string, queryType: string = 'FULL'): void {
    this.productResult$ = this.fetchProduct(technicalName, queryType);
    if (queryType === 'PREVIEW') {
      this.getProductDetails(technicalName, this.countrySelected ?? 'US');
    }
  }

  /**
   * Checks if the current user is authorized based on roles from the JWT token.
   */
  getAuthorizedRoles(): void {
    getAuthorized(this.authStorageService, this.configService, this);
  }

  convertUrlToCurrentHostname = convertUrlToCurrentHostname;

  open(url: string) {
    window.open(url, '_blank');
  }

  /**
   * Checks if a value is an array.
   * @param item The element to check.
   * @returns True if the item is an array, otherwise false.
   */
  isArray(item: any): boolean {
    return Array.isArray(item);
  }

  /**
   * Returns the keys of an object.
   * @param item The object to get keys from.
   * @returns Array of keys as strings.
   */
  objectKeys(item: any): string[] {
    return item ? Object.keys(item) : [];
  }

  /**
   * Checks if a value is a primitive data type (not an object).
   * @param item The element to check.
   * @returns True if the item is a primitive type, otherwise false.
   */
  isPrimitive(item: any): boolean {
    return item === null || item === undefined ? false : typeof item !== 'object';
  }

  /**
   * Fetches product details by technical name and country.
   * @param technicalName The technical name of the product.
   * @param country The country code.
   */
  private getProductDetails(technicalName: string, country: string) {
    this.productDetails$ = this.fetchProductDetails(technicalName, country);
  }

  /**
   * Fetches product details from the service.
   * @param technicalName The technical name of the product.
   * @param country The country code.
   * @returns Observable containing the product details.
   */
  private fetchProductDetails(technicalName: string, country: string): Observable<ProductDetails> {
    return this.productDetailsService
      .getProduct(technicalName, country ?? 'US')
      .pipe(map((response: any) => response?.product ?? undefined));
  }
}
